import { removeHtmlTag } from '@exam-management/common/utils';
import { FormLeavingListener } from '@exam-management/components';
import {
  GetQuestionCreationTaskByIdDocument,
  useCreateItemSetWorkflowMutation,
} from '@exam-management/graphql';
import {
  QBankStructureLevelCategory,
  type QBankStructureLevel,
} from '@exam-management/qbs-provider';
import {
  type ItemSetCreationFormInput,
  getLocationDefaultValue,
  getLocationSchema,
} from '@exam-management/qcontent-management';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { useQuestionCreationItemSetSearchParam } from '@exam-management/question-creation-task-question-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { matchPath, useLocation } from 'react-router-dom';
import { z } from 'zod';

const ITEM_SET_NAME_MAX_LENGTH = 256;

const DEFAULT_ITEM_SET_FORM_VALUES = {
  details: {
    name: '',
    paragraph: '',
  },
};

interface ItemSetQuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
}

export default function ItemSetQuestionCreationForm(
  props: Readonly<ItemSetQuestionCreationFormProps>,
) {
  const { children, qbsLevels } = props;
  const { task } = useQuestionCreationTaskDetails();
  const [{ size }] = useQuestionCreationItemSetSearchParam();
  const defaultLocationValues = getLocationDefaultValue(qbsLevels);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const matchedMyTask = matchPath('/my-task/question-creation/*', pathname);

  const [createItemSetWorkflow] = useCreateItemSetWorkflowMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Create Item Set',
        variant: 'error',
      });
    },
  });

  const createItemSetFormSchema = z.object({
    details: z.object({
      name: z
        .string()
        .trim()
        .min(1, { message: 'กรุณากรอกชื่อ item set' })
        .max(ITEM_SET_NAME_MAX_LENGTH, {
          message: `ชื่อ item set ต้องมีความยาวไม่เกิน ${ITEM_SET_NAME_MAX_LENGTH} ตัวอักษร`,
        }),
      paragraph: z.string().refine(
        (value) => {
          const details = removeHtmlTag(value, {
            customTag: 'p',
          }).trim();
          return details.length >= 1;
        },
        {
          message: 'กรุณากรอกบทความ',
        },
      ),
    }),
    location: z.object({
      [QBankStructureLevelCategory.Part]: getLocationSchema(
        QBankStructureLevelCategory.Part,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Subject]: getLocationSchema(
        QBankStructureLevelCategory.Subject,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Group]: getLocationSchema(
        QBankStructureLevelCategory.Group,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Chapter]: getLocationSchema(
        QBankStructureLevelCategory.Chapter,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Topic]: getLocationSchema(
        QBankStructureLevelCategory.Topic,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.SubTopic]: getLocationSchema(
        QBankStructureLevelCategory.SubTopic,
        qbsLevels,
      ),
    }),
  });

  const formContext = useForm<ItemSetCreationFormInput>({
    defaultValues: {
      ...DEFAULT_ITEM_SET_FORM_VALUES,
      location: {
        ...defaultLocationValues,
      },
    },
    resolver: zodResolver(createItemSetFormSchema),
  });

  const handleItemSetCreationFormSubmit = async (data: ItemSetCreationFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    const result = await createItemSetWorkflow({
      variables: {
        input: {
          taskDetails: {
            taskId: task.id,
            targetSize: size ?? 0,
          },
          refQBankStructureId: task?.metadata?.requirement.qBankStructure?.id ?? '',
          qBankStructureLevelItemId,
          paragraph: parseContentForApi(data.details.paragraph),
          name: data.details.name,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: (data) => {
        showSnackbar({
          message: 'Item Set created successfully',
          variant: 'success',
        });

        const itemSetDetailsPath = matchedMyTask
          ? '/my-task/question-creation/:taskId/item-set/:questionId'
          : '/task-management/question-creation/:taskId/item-set/:questionId';

        navigate(itemSetDetailsPath, {
          params: {
            taskId: task.id,
            questionId: data?.qctCreateItemSetWorkflow?.id,
          },
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleItemSetCreationFormSubmit)}>{children}</Form>
      <FormLeavingListener onSubmit={handleItemSetCreationFormSubmit} />
    </FormProvider>
  );
}
